<template>
    <div style="width: 100%;">
        <transition-group
            name="list"
            tag="div"
            class="attachments-grid"
            :class="{ '-dragging': dragging }"
            @dragenter="dragEnter"
            @dragleave="dragLeave"
            @dragover.prevent
            @drop.prevent="drop"
        >
            <Attachment
                :editing="true"
                :drag="dragging"
                @action="actionEmitted"
                :index="index"
                :type="attachmentType"
                :attachment="attachment"
                v-for="(attachment, index) in convertedAttachments"
                :key="index"
            />
            <div
                :class="{ '-error': error, '-dragging': dragging }"
                @click="$refs.fileInput.click()"
                class="add-attachment"
                key="add-attachment"
                v-if="
                    !(convertedAttachments.length > 0 && section.single) ||
                        !section.single
                "
            >
                <transition mode="out-in" name="fade">
                    <i v-if="!loading" class="material-icons">add</i>
                    <div class="dot-loader" v-if="loading">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </transition>
            </div>
        </transition-group>
        <div class="upload-progress-bar" :class="{ '-visible': loading }">
            <div :style="{ width: `${uploadProgress}%` }"></div>
        </div>
        <input
            @change.prevent="loadLocalFiles"
            class="file-input"
            :multiple="!section.single"
            ref="fileInput"
            style="display: none;"
            tabindex="-1"
            type="file"
            :accept="`${acceptedFiles}/*`"
        />
        <transition name="fade">
            <FileModal
                :file="selectedAttachment"
                @close="selectedAttachment = {}"
                v-if="selectedAttachment.path"
            ></FileModal>
        </transition>
    </div>
</template>
<script>
export default {
    components: {
        Attachment: () => import('@/components/utils/Attachment')
    },
    props: {
        value: [Array, String],
        section: Object,
        error: Boolean
    },
    data() {
        return {
            shouldDisplayCarousel: false,
            selectedAttachment: '',
            dragging: false,
            uploadProgress: 0,
            loading: false,
            localFiles: []
        }
    },
    computed: {
        acceptedFiles() {
            return this.section.accept ? this.section.accept : '*'
        },
        convertedAttachments() {
            const assets = !this.section.single
                ? this.value
                : this.value
                ? [this.value]
                : []
            const loadedAttachments = assets.map(asset => {
                return {
                    path: asset,
                    type: this.$Utils.getFileType(asset),
                    name: this.$Utils.getAttachmentName(asset)
                }
            })
            return [...loadedAttachments, ...this.localFiles]
        },
        attachmentType() {
            switch (true) {
                case this.section.avatar:
                    return 'avatar'
                case this.section.single:
                    return 'single'
                default:
                    return
            }
        }
    },
    methods: {
        drop(event) {
            this.loadLocalFiles(event, true)
            this.dragging = false
        },
        dragEnter() {
            this.dragging = true
        },
        dragLeave() {
            this.dragging = false
        },
        handleProgress(progress) {
            this.uploadProgress = progress
        },
        async loadLocalFiles(e, drop) {
            if (this.loading || !e || this.section.loading) {
                return
            }
            this.section.loading = true
            this.loading = true
            this.$assetUploader
                .getAssetFromFile(e, drop)
                .then(files => {
                    this.localFiles.push(...files)
                    if (this.section.autoUpload) {
                        this.$nextTick(() => {
                            this.uploadFiles()
                        })
                    }
                })
                .catch(error => {
                    this.$Network.handleError(error)
                })
                .then(() => {
                    this.section.loading = false
                    this.loading = false
                })
        },
        async uploadFiles() {
            return new Promise(resolve => {
                if (this.localFiles.length == 0) {
                    return resolve()
                }
                this.uploadProgress = 0
                this.loading = true
                this.section.loading = true
                return this.$assetUploader
                    .uploadLocalAssets(this.localFiles, 'public', progress =>
                        this.handleProgress(progress)
                    )
                    .then(response => {
                        if (this.section.single) {
                            this.$emit('input', response[0])
                            if (this.section.autoUpload) {
                                this.$emit('confirmInput', response[0])
                            }
                        } else {
                            this.$emit('input', [...this.value, ...response])
                        }
                    })
                    .catch(error => {
                        this.$Network.handleError(error, this.errors)
                    })
                    .then(() => {
                        this.localFiles = []
                        this.loading = false
                        this.section.loading = false
                        this.uploadProgress = 0
                        resolve()
                    })
            })
        },
        actionEmitted(action) {
            switch (action.name) {
                case 'edit':
                    this.$refs.fileInput.click()
                    break
                case 'delete':
                    this.deleteAttachment(action.id)
                    break
            }
        },
        deleteAttachment(attachment) {
            if (attachment.local) {
                this.localFiles.splice(attachment.index, 1)
            } else {
                if (this.section.single) {
                    this.$emit('input', '')
                } else {
                    const newFiles = JSON.parse(JSON.stringify(this.value))
                    newFiles.splice(attachment.index, 1)
                    this.$emit('input', newFiles)
                }
            }
        }
    }
}
</script>
<style lang="scss" scoped>
.section-attachments {
    padding: 0.5rem;
    box-sizing: border-box;
}
.attachments-grid {
    transition: $ease;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-auto-rows: 5.5rem;
    grid-gap: 0.5rem;
    width: 100%;
    min-width: 100%;
    position: relative;
    padding: 0.5rem;
    box-sizing: border-box;
    border: 0.1rem dashed transparent;
    border-radius: $input-radius;

    &.-dragging {
        border-color: $input;
        background: darken($background, 2%);
    }
}
.attachment-wrap {
    overflow: hidden;

    &:not(:last-of-type) {
        margin-right: 0.5rem;
    }
}
.add-attachment {
    position: relative;
    width: 100%;
    min-width: 4rem;
    height: 4rem;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: $small-radius;
    overflow: hidden;
    border: solid 0.1rem $border-accent;

    &.-dragging {
        pointer-events: none;
    }

    &.-error {
        border-color: $accent-red;
    }
}
.add-attachment:hover img {
    transform: translateY(0.1rem);
}
.add-attachment img {
    transition: $ease;
    height: 2rem !important;
    width: 2rem !important;
}
.dot-loader {
    margin-top: -25%;
    font-size: 4rem;
}
.image-placeholder {
    width: 5rem;
    height: 5rem;
    display: flex;
    justify-content: center;
    align-items: center;
}
.image-placeholder i {
    margin: 0;
    font-size: 2rem;
}
</style>
